import { z } from "zod";
import type { SectionDef } from "./_types";
import {
  HOME_CONFIG_DEFAULTS,
  HOME_EVENTS_MODES,
  HOME_LIMITS,
  HOME_TITLE_MAX,
} from "./home-config";

const AppShape = {
  email_tagline:       z.string().min(1).max(500),
  // KLUB-92 (#291): a chave de link de pagamento foi removida desta seção. O
  // destino é DERIVADO do domínio do tenant em código — `appRedirectUrl(branding,
  // "payment-management")` —, nunca configurado: a chave apontava para um
  // domínio à venda e o botão de cobrança virou um 404.
  club_whatsapp:       z.string().regex(/^\+\d{10,15}$/),
  grace_period_days:   z.number().int().min(0).max(365),
  invitation_ttl_days: z.number().int().min(1).max(30),
  onboarding_welcome_completed_at: z.string().datetime().nullable(),
  mobile_assets_ready_at:          z.string().datetime().nullable(),
  mobile_published_at:             z.string().datetime().nullable(),
  // KLUB-9 (#167) — prospect subscription popup. Curated per-tenant content
  // (enabled toggle + copy + benefits list), stored as a single JSONB object
  // under the `app.prospect_popup` key. Read at runtime by the mobile app
  // (NOT codegen-baked). `enabled` defaults false for a safe rollout.
  prospect_popup: z.object({
    enabled:  z.boolean(),
    headline: z.string().min(1).max(120),
    subtitle: z.string().min(1).max(240),
    benefits: z.array(z.string().max(80)).max(8),
  }),
  // #197 (KLUB-13) R15 — club-wide switch for manual (name-search) check-in at
  // the door. Two layers decide: the tenant permits it here, and then
  // activities.allow_manual_checkin still decides per activity. With this off,
  // staff completes check-in by QR only, whatever the activity says.
  //
  // Defaults false = fail closed. Note that write-tenant-setting DELETES the
  // row when the saved value equals the default, so readers MUST treat a
  // missing row as `false` rather than as unset config.
  manual_checkin_enabled: z.boolean(),
  // KLUB-64 (#252) — the whole Home tab of the admin, per tenant. Used to live
  // in `system_settings`, a three-column global table whose only SELECT policy
  // requires `profiles.role = 'staff'`: the config was shared across tenants
  // AND read back empty for every member and prospect. Stored here as a single
  // JSONB object and read at runtime by the app (NOT from the codegen-baked
  // lib/settings.ts, which would freeze it at EAS build time — R39).
  //
  // The shape, the ranges and the default all come from ./home-config so the
  // numbers exist once; the Deno mirrors restate them and home-config.test.ts
  // fails on drift.
  home: z.object({
    events_title:       z.string().min(1).max(HOME_TITLE_MAX).nullable(),
    // KLUB-131 (#341) — the feed section's title. Nullable like events_title,
    // but null resolves to the shared literal HOME_FEED_TITLE_FALLBACK rather
    // than to the tenant vocabulary: there is no vocabulary noun for a post,
    // and one would only reach mobile through a new build (KTD2).
    feed_title:         z.string().min(1).max(HOME_TITLE_MAX).nullable(),
    events_mode:        z.enum(HOME_EVENTS_MODES),
    events_count:       z.number().int().min(HOME_LIMITS.events_count.min).max(HOME_LIMITS.events_count.max),
    events_period_days: z.number().int().min(HOME_LIMITS.events_period_days.min).max(HOME_LIMITS.events_period_days.max),
    feed_limit:         z.number().int().min(HOME_LIMITS.feed_limit.min).max(HOME_LIMITS.feed_limit.max),
    hero_limit:         z.number().int().min(HOME_LIMITS.hero_limit.min).max(HOME_LIMITS.hero_limit.max),
    rotate_seconds:     z.number().int().min(HOME_LIMITS.rotate_seconds.min).max(HOME_LIMITS.rotate_seconds.max),
  }),
  // KLUB-264 — where the "Todos" chip sits in the Atividades / Benefícios strip,
  // on the type order scale: "Todos" goes right before the type whose
  // sort_order equals the value, and N+1 puts it last. null means first, which
  // is also what a missing row means, since write-tenant-setting deletes a row
  // equal to its default. Read at runtime by the app (NOT codegen-baked).
  //
  // Written only by the reorder_*_type_chips RPCs, in the same transaction that
  // renumbers the types. The upper bound depends on the type count, so the RPC
  // enforces it, not this schema. `hidden` keeps the generic editor off them.
  activities_all_chip_position: z.number().int().positive().nullable(),
  benefits_all_chip_position:   z.number().int().positive().nullable(),
};

export const app = {
  schema: z.object(AppShape),
  defaults: {
    email_tagline:       "Klubora — gestão de clubes e comunidades.",
    club_whatsapp:       "+5511000000000",
    grace_period_days:   30,
    invitation_ttl_days: 7,
    onboarding_welcome_completed_at: null,
    mobile_assets_ready_at:          null,
    mobile_published_at:             null,
    // Generic defaults (no tenant name). `benefits` needs a concrete `[]`
    // because SectionDef.defaults is Required<>. Cast keeps it `string[]`
    // rather than the `readonly []` that `as const` would otherwise produce.
    prospect_popup: {
      enabled:  false,
      headline: "Torne-se membro",
      subtitle: "Desbloqueie tudo o que só quem é membro acessa.",
      benefits: [] as string[],
    },
    manual_checkin_enabled: false,
    // Same lockstep warning as prospect_popup above: this default and the
    // SCHEMAS.app schema key in write-tenant-setting/validators.ts must land
    // together, or EVERY `app` write breaks — not just the home's.
    home: { ...HOME_CONFIG_DEFAULTS },
    // KLUB-264 — same lockstep rule. null = "Todos" first, as before.
    activities_all_chip_position: null,
    benefits_all_chip_position:   null,
  },
  meta: {
    email_tagline:       { label: "Email tagline",         group: "email",      widget: "text" },
    club_whatsapp:       { label: "Club WhatsApp number",  group: "contact",    widget: "text" },
    grace_period_days:   { label: "Grace period (days)",   group: "policy",     widget: "number" },
    invitation_ttl_days: { label: "Invitation TTL (days)", group: "policy",     widget: "number" },
    onboarding_welcome_completed_at: { label: "Onboarding welcome completed at", group: "onboarding", widget: "text" },
    mobile_assets_ready_at:          { label: "Mobile assets ready at",          group: "onboarding", widget: "text" },
    mobile_published_at:             { label: "Mobile published at",             group: "onboarding", widget: "text" },
    prospect_popup:                  { label: "Popup de assinatura (prospects)",  group: "prospect_popup", widget: "text" },
    manual_checkin_enabled:          { label: "Permitir check-in manual",         group: "checkin",    widget: "boolean" },
    home:                            { label: "Home do app",                      group: "home",       widget: "text" },
    activities_all_chip_position:    { label: "Posição do chip Todos (atividades)", group: "type_chips", widget: "number", hidden: true },
    benefits_all_chip_position:      { label: "Posição do chip Todos (benefícios)", group: "type_chips", widget: "number", hidden: true },
  },
  forbiddenLiterals: [],
} as const satisfies SectionDef<typeof AppShape>;
